import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useState, useEffect } from 'react';
import { fetchMails, resendVerificationMail } from '../api/mails';
import Pagination from '../components/Pagination';
import { can } from '../utils/permissions';
import './MailsPage.css';

const reasonLabel = {
  mailauth: '이메일 인증',
  idfind: '아이디 찾기',
  resetpassword: '비밀번호 재설정',
};

const getReasonBadgeClass = (reason) => {
  if (reason === 'mailauth') return 'verify';
  if (reason === 'resetpassword') return 'password';
  if (reason === 'idfind') return 'idfind';
  return 'notice';
};

const PAGE_SIZE = 50;

function MailsPage() {
  const { t } = useLanguage();
  const canResendMail = can('mail.resend');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendUserUuid, setResendUserUuid] = useState('');
  const [resendUsername, setResendUsername] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendResult, setResendResult] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [doneModalOpen, setDoneModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadMails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const offset = (currentPage - 1) * PAGE_SIZE;
      const res = await fetchMails({
        offset,
        limit: PAGE_SIZE,
        search: searchTerm.trim(),
      });
      setMails(res.data.mails ?? []);
      setTotalCount(Number(res.data.total ?? 0));
    } catch (err) {
      setError(err.response?.data?.message || '메일 목록을 불러오지 못했습니다.');
      setMails([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadMails();
  }, [loadMails]);

  const filteredMails = mails.filter((mail) => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return true;

    return [
      mail.username,
      mail.email,
    ].some((value) => String(value ?? '').toLowerCase().includes(keyword));
  });

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const datePart = `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
    if (withTime) {
      return `${datePart} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return datePart;
  };

  const getResultStatus = (result) => {
    if (!result) return 'unknown';
    const code = String(result).trim().match(/^(\d{3})/)?.[1];
    if (!code) return 'unknown';
    if (code.startsWith('2')) return 'success';
    if (code.startsWith('4')) return 'temporaryFail';
    if (code.startsWith('5')) return 'fail';
    return 'unknown';
  };

  const renderResultBadge = (result) => {
    const status = getResultStatus(result);
    if (status === 'success') return <span className="status-badge success">{t("성공")}</span>;
    if (status === 'temporaryFail') return <span className="status-badge pending">{t("임시 실패")}</span>;
    if (status === 'fail') return <span className="status-badge fail">{t("실패")}</span>;
    return <span className="status-badge pending">{t("미확인")}</span>;
  };

  const getResendErrorMessage = (err) => (
    err?.response?.data?.message || err?.message || '인증 메일 재전송에 실패했습니다.'
  );

  const openResendModal = (mail = {}) => {
    if (!canResendMail) return;
    setResendUserUuid(mail.userUuid || '');
    setResendUsername(mail.username || '');
    setResendEmail(mail.email || '');
    setResendError('');
    setResendResult('');
    setResendModalOpen(true);
  };

  const closeResendModal = () => {
    if (resendLoading) return;
    setResendModalOpen(false);
    setConfirmModalOpen(false);
    setResendError('');
  };

  const handleResendVerificationMail = async () => {
    if (!canResendMail) return;
    if (!resendUserUuid.trim()) return;

    try {
      setResendLoading(true);
      setResendError('');
      const result = await resendVerificationMail({
        userUuid: resendUserUuid.trim(),
      });

      setResendResult(result?.result || '');
      await loadMails();
      setConfirmModalOpen(false);
      setDoneModalOpen(true);
    } catch (err) {
      console.error('failed to resend verification email:', err);
      setResendError(getResendErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  };

  const getPaginationInfo = () => {
    if (searchTerm) {
      return `현재 페이지 검색 결과 ${filteredMails.length.toLocaleString()}건 / 서버 전체 ${totalCount.toLocaleString()}건`;
    }

    if (totalCount === 0) return '총 0건 표시';

    const start = (currentPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(start + mails.length - 1, totalCount);
    return `총 ${totalCount.toLocaleString()}건 중 ${start.toLocaleString()}-${end.toLocaleString()} 표시`;
  };

  return (
    <div className="mails-page">
      {/* 헤더 */}
      <div className="page-header">
        <div>
          <h2 className="page-title">{t("메일 관리")}</h2>
          <p className="page-subtitle">{t("메일 발송 내역을 조회하고, 인증 메일을 재전송할 수 있습니다.")}</p>
        </div>
        <div className="header-actions">
          <form className="search-form" onSubmit={handleSearchSubmit}>
            <div className="search-box">
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t("username, 이메일 입력 후 Enter")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <button className="btn-search" type="submit" disabled={loading}>
              {t("검색")}
            </button>
          </form>
          <button
            className="btn-resend"
            disabled={!canResendMail}
            title={!canResendMail ? '인증 메일 재발송 권한이 없습니다.' : undefined}
            onClick={() => openResendModal()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13"></path>
              <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
            </svg>
            {t("인증 메일 재전송")}
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="table-card">
        <table className="mails-table">
          <thead>
            <tr>
              <th>username</th>
              <th>email</th>
              <th>reason</th>
              <th>{t("발송일")}</th>
              <th>{t("발송결과")}</th>
              {/* <th>삭제 여부</th> */}
              <th className="col-action">{t("관리")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>{t("데이터를 불러오는 중입니다...")}</td></tr>
            ) : error ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: '60px 0', color: '#ef4444' }}>{error}</td></tr>
            ) : filteredMails.length > 0 ? (
              filteredMails.map(mail => (
                <tr
                  key={mail.uuid}
                  className="mail-row"
                  onClick={() => setSelectedMail(mail)}
                >
                  <td className="detail-text" style={{ fontWeight: 600 }}>{mail.username}</td>
                  <td className="detail-text">{mail.email}</td>
                  <td>
                    <span className={`reason-badge ${getReasonBadgeClass(mail.reason)}`}>
                      {mail.reasonLabel ?? reasonLabel[mail.reason] ?? mail.reason}
                    </span>
                  </td>
                  <td className="detail-text">{formatDate(mail.createdAt)}</td>
                  <td>{renderResultBadge(mail.result)}</td>
                  {/*
                  <td>
                    {mail.deletedAt
                      ? <span className="status-badge deleted">삭제</span>
                      : <span className="detail-text">-</span>}
                  </td>
                  */}
                  <td className="col-action">
                    <button className="action-btn" onClick={(e) => {
                      e.stopPropagation();
                      setSelectedMail(mail);
                    }}>
                      {t("상세 보기")}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                  "{searchTerm}" 에 대한 검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          total={totalCount}
          pageSize={PAGE_SIZE}
          info={getPaginationInfo()}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Drawer Overlay */}
      {selectedMail && (
        <div className="drawer-overlay" onClick={() => setSelectedMail(null)}></div>
      )}

      {/* Drawer Panel */}
      <div className={`detail-drawer ${selectedMail ? 'open' : ''}`}>
        {selectedMail && (
          <>
            <div className="drawer-header">
              <h3>{t("메일 상세 정보")}</h3>
              <button className="drawer-close-btn" onClick={() => setSelectedMail(null)}>
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div className="drawer-content">
              {/* 요약 */}
              <div className="drawer-profile-card">
                <div className="drawer-profile-info">
                  <div className="drawer-name">
                    {selectedMail.username}
                    <span className={`reason-badge ${getReasonBadgeClass(selectedMail.reason)}`}>
                      {selectedMail.reasonLabel ?? reasonLabel[selectedMail.reason] ?? selectedMail.reason}
                    </span>
                    {renderResultBadge(selectedMail.result)}
                  </div>
                  <div className="drawer-sub">{selectedMail.email}</div>
                </div>
              </div>

              {/* 섹션 1: 메일 기본 정보 */}
              <div className="drawer-section">
                <h4>{t("메일 기본 정보")}</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>UUID</label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedMail.uuid}</span>
                  </div>
                  <div className="info-item">
                    <label>email</label>
                    <span>{selectedMail.email}</span>
                  </div>
                  <div className="info-item">
                    <label>username</label>
                    <span>{selectedMail.username}</span>
                  </div>
                  <div className="info-item">
                    <label>User UUID</label>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedMail.userUuid}</span>
                  </div>
                  <div className="info-item">
                    <label>reason</label>
                    <span>
                      <span className={`reason-badge ${getReasonBadgeClass(selectedMail.reason)}`}>
                        {selectedMail.reasonLabel ?? reasonLabel[selectedMail.reason] ?? selectedMail.reason}
                      </span>
                    </span>
                  </div>
                  <div className="info-item">
                    <label>result</label>
                    <span>{renderResultBadge(selectedMail.result)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("result 원문")}</label>
                    <span>{selectedMail.result || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("발송일 (CreatedAt)")}</label>
                    <span>{formatDate(selectedMail.createdAt, true)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("수정일 (UpdatedAt)")}</label>
                    <span>{formatDate(selectedMail.updatedAt, true)}</span>
                  </div>
                  <div className="info-item">
                    <label>{t("삭제일 (DeletedAt)")}</label>
                    <span>{selectedMail.deletedAt ? formatDate(selectedMail.deletedAt, true) : '-'}</span>
                  </div>
                </div>
              </div>

              {/* 섹션 2: 메일 내용 */}
              <div className="drawer-section">
                <h4>{t("메일 내용 (Content)")}</h4>
                <div className="content-preview">
                  {selectedMail.content || '(내용 없음)'}
                </div>
              </div>

              <div className="drawer-section">
                <h4>{t("메일 관리 (관리자 기능)")}</h4>
                <div className="admin-action-card">
                  <div className="action-text">
                    <strong>{t("인증 메일 재전송")}</strong>
                    <p>{canResendMail ? '이 사용자의 이메일 인증 토큰을 재생성하고 인증 메일을 발송합니다.' : '인증 메일 재발송 권한이 없습니다.'}</p>
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={() => openResendModal(selectedMail)}
                    disabled={!selectedMail.userUuid || !canResendMail}
                  >
                    {t("재전송")}
                  </button>
                </div>
              </div>

            </div>
          </>
        )}
      </div>

      {/* 인증 메일 재전송 모달 */}
      {resendModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeResendModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("인증 메일 재전송")}</h3>
              <button className="drawer-close-btn" onClick={closeResendModal}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>User UUID</label>
                <input
                  type="text"
                  placeholder="75376934-5990-424b-..."
                  value={resendUserUuid}
                  onChange={(e) => setResendUserUuid(e.target.value)}
                  disabled={resendLoading}
                />
              </div>
              {resendUsername && (
                <div className="modal-info">
                  <label>username</label>
                  <span>{resendUsername}</span>
                </div>
              )}
              {resendEmail && (
                <div className="modal-info">
                  <label>email</label>
                  <span>{resendEmail}</span>
                </div>
              )}
              <p className="modal-warning">
                해당 사용자의 이메일 인증 토큰을 재생성하고 인증 메일을 발송합니다. User UUID를 정확히 확인하세요.
              </p>
              {resendError && <p className="modal-error">{resendError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={resendLoading} onClick={closeResendModal}>{t("취소")}</button>
              <button
                className="btn-primary"
                disabled={resendLoading || !resendUserUuid.trim()}
                onClick={() => { setResendModalOpen(false); setConfirmModalOpen(true); }}
              >
                {t("재전송")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 재전송 확인 모달 */}
      {confirmModalOpen && (
        <>
          <div className="modal-overlay" onClick={() => { if (!resendLoading) setConfirmModalOpen(false); }}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("발송 확인")}</h3>
              <button className="drawer-close-btn" onClick={() => { if (!resendLoading) setConfirmModalOpen(false); }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <label>User UUID</label>
                <span>{resendUserUuid}</span>
              </div>
              {resendUsername && (
                <div className="modal-info">
                  <label>username</label>
                  <span>{resendUsername}</span>
                </div>
              )}
              {resendEmail && (
                <div className="modal-info">
                  <label>email</label>
                  <span>{resendEmail}</span>
                </div>
              )}
              <div className="modal-info">
                <label>{t("사유")}</label>
                <span>{t("인증 메일 재전송")}</span>
              </div>
              <div className="modal-info">
                <label>{t("내용")}</label>
                <span>이메일 인증 코드 발송</span>
              </div>
              <div className="modal-info">
                <label>{t("발송 일시")}</label>
                <span>{new Date().toLocaleString('ko-KR')}</span>
              </div>
              <p className="modal-warning">
                위 내용으로 인증 메일을 발송합니다. 계속하시겠습니까?
              </p>
              {resendError && <p className="modal-error">{resendError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={resendLoading} onClick={() => setConfirmModalOpen(false)}>{t("취소")}</button>
              <button className="btn-primary" disabled={resendLoading || !canResendMail} onClick={handleResendVerificationMail}>
                {resendLoading ? t("발송 중...") : t("발송 확인")}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 발송 완료 모달 */}
      {doneModalOpen && (
        <>
          <div className="modal-overlay" onClick={() => setDoneModalOpen(false)}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("발송 완료")}</h3>
              <button className="drawer-close-btn" onClick={() => setDoneModalOpen(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-success">
                {t("인증 메일이 발송되었습니다.")}
              </p>
              {resendResult && (
                <div className="modal-info">
                  <label>{t("발송 결과")}</label>
                  <span>{resendResult}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setDoneModalOpen(false)}>{t("확인")}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MailsPage;
